import type { NotificationSettingValue, NotificationType } from '@frontend/api-types';
import { match } from '@frontend/duck-tape';
import { SwitchInput, Text, XStack, YStack } from '@frontend/web-react';
import type { ClassName } from '@frontend/web-utils';
import { concatClasses } from '@frontend/web-utils';

type NotificationCategoryProps = {
  category: string;
  className?: ClassName;
  isTitleSwitchActive?: boolean;
  onToggle: (category: string, type: NotificationType) => void;
  onToggleTitleSwitch?: () => void;
  options?: NotificationType[];
  subtitle: string;
  title: string;
  value: NotificationSettingValue;
};

export const NotificationCategory = ({
  category,
  className,
  isTitleSwitchActive,
  onToggle,
  onToggleTitleSwitch,
  options,
  subtitle,
  title,
  value,
}: NotificationCategoryProps) => {
  const getToggleLabel = (type: NotificationType) =>
    match(type)
      .with('email', () => 'Email')
      .with('sms', () => 'SMS')
      .with('push', () => 'Push notifications')
      .exhaustive();

  const handleToggle = (type: NotificationType) => {
    onToggle(category, type);
  };

  return (
    <YStack className={concatClasses('gap-y-md', className)}>
      <YStack className="flex-1" gap="xs">
        <XStack className="justify-between">
          <Text type="h5">{title}</Text>
          {onToggleTitleSwitch ? <SwitchInput checked={isTitleSwitchActive} onChange={onToggleTitleSwitch} /> : null}
        </XStack>
        <Text type="p-sm">{subtitle}</Text>
      </YStack>
      {!isTitleSwitchActive && onToggleTitleSwitch ? null : (
        <YStack gapY="md">
          {options?.map((type) => (
            <SwitchInput
              checked={value.includes(type)}
              key={type}
              label={getToggleLabel(type)}
              onChange={() => handleToggle(type)}
            />
          ))}
        </YStack>
      )}
    </YStack>
  );
};
